import { useRouter } from 'next/router';
import React, { FC, forwardRef, ForwardRefRenderFunction, LegacyRef } from 'react';

import { Box, Grid, Typography, useTheme } from '@mui/material';

import Image from '@components/atoms/image';
import LikeAndShareCourse from '@components/share/custom/likeAndShareCourse';
import CourseReview from '@components/templates/courseDetail/courseAcademicBanner/header/bannerDetail/courseReview';
import CourseRegularHeaderPurchaseDetailSkeleton from '@components/templates/courseDetail/courseAcademicBanner/header/bannerDetail/skeleton';
import { CourseHeaderBannerDetailContainer } from '@components/templates/courseDetail/courseAcademicBanner/header/bannerDetail/style';
import { getDegreeLandingPageUrl } from '@helpers/url';
import { useBreakpointV2 } from '@helpers/use';
import useCourseDetail from '@hooks/course/useCourseDetail';
import { CourseEntity } from '@redux/course/type/courseDetailType';

const CourseHeaderBannerDetail = (props: { courseDetailSeo: CourseEntity }, courseNameRef: LegacyRef<HTMLElement>) => {
  const { isMobile } = useBreakpointV2();
  const { courseDetailSeo } = props;
  const { name, degrees, university } = courseDetailSeo;
  const { review, isCourseDetailLoading, universityLogoUrl } = useCourseDetail();
  const theme = useTheme();
  const router = useRouter();

  if (isCourseDetailLoading) {
    return <CourseRegularHeaderPurchaseDetailSkeleton />;
  }

  return (
    <CourseHeaderBannerDetailContainer>
      <Grid sx={{ justifyContent: 'center', alignItems: 'center' }} columnSpacing={{ lg: 6, md: 4, xs: 1 }} container>
        <Grid lg={10} md={10} xs={9} item>
          <Box sx={{ display: 'flex', gap: '24px' }}>
            {!isMobile && (
              <Image
                style={{
                  height: '64px',
                  width: '64px',
                  borderRadius: '4px',
                  objectFit: 'contain',
                  border: '1px solid #0000001F',
                  backgroundColor: 'white',
                }}
                src={universityLogoUrl}
                alt={university.name}
                errorImage="/static/icons/default-university-logo.svg"
              />
            )}

            <Box>
              <Typography variant="h5" color={theme.palette.textOnPrimary.secondary}>
                {university.name}
              </Typography>

              <Box sx={{ mb: '12px' }} ref={courseNameRef as React.RefObject<HTMLElement>}>
                <Typography
                  sx={{
                    typography: {
                      md: 'h1',
                      xs: 'h4',
                    },
                    color: theme.palette.textOnPrimary.primary,
                    lineHeight: {
                      lg: '43px',
                      md: '36px',
                    },
                  }}
                >
                  {name}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid lg={2} md={2} xs={3} item sx={{ pt: 0 }}>
          <LikeAndShareCourse
            courseDetailSeo={courseDetailSeo}
            sx={{ justifyContent: 'flex-end', minWidth: 'unset' }}
          />
        </Grid>
      </Grid>

      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: '11px', marginTop: { lg: '11px', md: '4px', xs: '11px' } }}
      >
        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {degrees &&
            degrees.map((degree) => (
              <Box
                sx={{
                  cursor: 'pointer',
                  color: theme.palette.common.white,
                  border: `1px solid ${theme.palette.textOnPrimary.primary}`,
                  height: 24,
                  fontSize: 18,
                  borderRadius: '20px',
                  px: 1.5,
                  whiteSpace: 'nowrap',
                  maxWidth: '-webkit-fill-available',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                key={degree.friendlyUrl}
                onClick={() => router.push(getDegreeLandingPageUrl(university.slug, degree.friendlyUrl))}
              >
                {degree.name}
              </Box>
            ))}
        </Box>

        {review ? (
          <CourseReview reviewAverageRating={review.reviewAverageRating} totalReview={review.totalReview} />
        ) : null}
      </Box>
    </CourseHeaderBannerDetailContainer>
  );
};

export default forwardRef(CourseHeaderBannerDetail);
